/**
 * The wall display — a screen on the kitchen wall.
 *
 *     A screen on the kitchen wall beats a notification nobody opens. **Runs on any
 *     tablet, TV or old monitor.**
 *
 * **An overlay on every screen, not a page of its own.** The prototype puts the button in
 * the header beside the family tabs, and that is right: the wall is a mode the product
 * enters, not somewhere a person navigates to and has to navigate back from. A tablet on a
 * shelf is opened once and left.
 *
 * Three things follow from nobody standing at it:
 *
 * * **It reloads itself.** A wall showing yesterday is worse than a blank one, so it
 *   refetches every few minutes and redraws at midnight without being asked.
 * * **It owns no data.** Every column is read from the feature that owns it, so the chore
 *   ticked on the wall is the same row the chore chart shows — there is no second truth in
 *   the kitchen.
 * * **It is almost entirely read-only.** The one thing it changes is a tick, and that goes
 *   to Chores & Rewards' own endpoint.
 *
 * The prototype's weather and its "Add by voice" button are absent: there is no forecast
 * service, and a wall that showed a made-up temperature would be wrong about the one thing
 * a parent uses it for at eight in the morning.
 */

import { wall as fetchWall } from '../api/wall.js';
import { tickChore } from '../api/chores.js';
import { esc } from '../lib/dom.js';

/** How often the wall refetches. Often enough to be current, rarely enough to be quiet. */
const EVERY = 5 * 60 * 1000;

let el = null;
let timer = null;
let data = null;

/**
 * Whether the photograph is up.
 *
 * **Held here rather than as a class on the div**, because the wall rebuilds its own
 * markup every time it refetches. Kept in the DOM, the screensaver switched itself off
 * every five minutes — on a kitchen tablet left overnight that is a bright green screen
 * at three in the morning instead of a photograph.
 */
let showingPhoto = false;

export function mountWall(root) {
  if (document.getElementById('wall')) return;

  el = document.createElement('div');
  el.id = 'wall';
  root.appendChild(el);

  el.addEventListener('click', onClick);
  document.addEventListener('keydown', onKey);
}

export async function openWall() {
  if (!el) return;

  el.classList.add('on');
  await load();

  // Only once it is open. A timer running behind every screen in the product to keep a
  // wall nobody is looking at up to date is a request every five minutes, for ever.
  if (!timer) timer = setInterval(load, EVERY);
}

export function closeWall() {
  if (!el) return;

  el.classList.remove('on');
  showPhoto(false);
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}

async function load() {
  try {
    data = await fetchWall();
  } catch {
    data = null;
  }
  draw();
}

function onKey(event) {
  if (event.key !== 'Escape' || !el || !el.classList.contains('on')) return;

  // Escape wakes the screensaver before it closes the wall, for the same reason: one key
  // press should undo the last thing that happened, not two things at once.
  if (showingPhoto) {
    showPhoto(false);
    return;
  }

  closeWall();
}

/** Raise or lower the photograph, and remember which, so a refetch does not undo it. */
function showPhoto(up) {
  showingPhoto = up;
  el?.querySelector('.wall-photo')?.classList.toggle('on', up);
}

async function onClick(event) {
  // **The photograph is checked first, and anywhere on it dismisses it.**
  //
  // It covers the whole wall when it is up — that is what a screensaver is — which means
  // it also covers the button that put it there. Toggling only from the footer left a
  // kitchen tablet with a photograph on it and no way back except reloading the page.
  // A screensaver you touch to wake is the behaviour everybody already expects.
  if (showingPhoto) {
    showPhoto(false);
    return;
  }

  const close = event.target.closest('[data-wall-close]');
  if (close) {
    closeWall();
    return;
  }

  const shots = event.target.closest('[data-wall-photo]');
  if (shots) {
    showPhoto(true);
    return;
  }

  const tick = event.target.closest('[data-wall-tick]');
  if (tick) {
    // Ticked here, recorded there. The chart is the only place a tick lives.
    tick.disabled = true;
    await tickChore(tick.dataset.wallTick).catch(() => {});
    await load();
  }
}

function draw() {
  if (!el) return;

  if (!data) {
    el.innerHTML = `
      <div class="wall-top">
        <div><div class="wall-date">Not reachable</div>
          <div class="wall-sub">The wall could not read the family just now</div></div>
      </div>
      <div class="wall-foot">
        <button type="button" data-wall-close>Close</button>
      </div>`;
    return;
  }

  el.innerHTML = `
    <div class="wall-photo${showingPhoto ? ' on' : ''}">${
      data.photo
        ? `<div class="cap">${esc(data.photo.caption)}</div>`
        : '<div class="cap">No photograph with a caption yet</div>'
    }</div>

    <div class="wall-top">
      <div>
        <div class="wall-date">${esc(data.day_name)}</div>
        <div class="wall-sub">${esc(data.date_label)}${
          data.place ? ` · ${esc(data.place)}` : ''
        }</div>
      </div>
    </div>

    <div class="wall-body">
      ${todayColumn()}
      ${choresColumn()}
      ${eatingColumn()}
    </div>

    <div class="wall-foot">
      <button type="button" data-wall-photo>◲ Photo screensaver</button>
      <span class="wall-sub" style="margin-left:auto;text-transform:none;letter-spacing:0">
        A screen on the kitchen wall beats a notification nobody opens. Runs on any
        tablet, TV or old monitor.
      </span>
      <button type="button" data-wall-close>Close</button>
    </div>`;
}

function todayColumn() {
  const rows = data.events || [];

  return `
    <div class="wall-col">
      <div class="wall-h">Today</div>
      <div style="overflow-y:auto">
        ${
          rows.length
            ? rows
                .map(
                  (row) => `
          <div class="wev">
            <span class="tm">${esc(row.time_display || row.time || '')}</span>
            <span class="who" style="background:${esc(row.colour || '#6B7A72')}"></span>
            <span>${esc(row.title)}${
              row.place ? ` <span class="wall-sub" style="text-transform:none">${esc(row.place)}</span>` : ''
            }</span>
          </div>`,
                )
                .join('')
            : '<div class="wev"><span>Nothing at a set time today.</span></div>'
        }
      </div>
      ${
        data.asking
          ? `<div class="wall-need">
               <b>${esc(data.asking.what)}</b> — ${esc(data.asking.day)} ${esc(
                 data.asking.kind,
               )} is still nobody's.
             </div>`
          : ''
      }
    </div>`;
}

function choresColumn() {
  const rows = data.chores || [];

  return `
    <div class="wall-col">
      <div class="wall-h">Chores</div>
      <div style="overflow-y:auto">
        ${
          rows.length
            ? rows
                .map(
                  (row) => `
          <button type="button" class="wch ${row.done ? 'done' : ''}"
                  data-wall-tick="${esc(row.id)}"
                  style="width:100%;background:none;border:0;color:inherit;font:inherit;
                         text-align:left;cursor:pointer">
            <span>${row.done ? '✓' : '☐'}</span>
            <span>${esc(row.what)} <span class="wall-sub"
              style="text-transform:none;letter-spacing:0">${esc(row.who)}</span></span>
            <span class="st">${row.stars} ★</span>
          </button>`,
                )
                .join('')
            : '<div class="wch"><span>Nothing on the chart today.</span></div>'
        }
      </div>

      <div style="margin-top:14px" class="wall-h">Stars</div>
      ${(data.stars || [])
        .map(
          (one) => `
        <div class="wch">
          <span>${esc(one.who)}</span>
          <span class="st">${one.balance} ★${
            one.towards ? ` · ${esc(one.towards)}` : ''
          }</span>
        </div>`,
        )
        .join('')}
    </div>`;
}

function eatingColumn() {
  const rows = data.meals || [];

  return `
    <div class="wall-col">
      <div class="wall-h">Eating</div>
      ${
        rows.length
          ? rows
              .map(
                (row) => `
        <div class="wmeal"><span>${esc(row.label)}</span>${
          row.what ? esc(row.what) : 'nothing planned'
        }</div>`,
              )
              .join('')
          : '<div class="wmeal"><span>Eating</span>nothing planned today</div>'
      }

      ${
        (data.also || []).length
          ? `<div style="margin-top:16px" class="wall-h">Also</div>
             ${data.also
               .map(
                 (one) =>
                   `<div class="wmeal"><span>${esc(one.what)}</span>${esc(one.said)}</div>`,
               )
               .join('')}`
          : ''
      }
    </div>`;
}
